import { Alert, Box, CircularProgress, Typography, alpha, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import { CostingComponent, type CostingPageResponse } from './CostingComponent';
import PricingInsights, { type PricingInsightsData } from './PricingInsights';

export type { PricingInsightsData };

type VenueDetailsPageSummaryProps = {
  pageResponse: Record<string, any>;
  loading?: boolean;
  error?: string | null;
};

const isCostingPageResponse = (value: Record<string, any>): value is CostingPageResponse =>
  Array.isArray(value.lineItems) && value.total != null && value.total !== '';

const VenueDetailsPageSummary = ({ pageResponse, loading = false, error = null }: VenueDetailsPageSummaryProps) => {
  const theme = useTheme();
  const normalizedResponse = pageResponse ?? {};
  const hasLiveBreakdown = isCostingPageResponse(normalizedResponse);
  const pricingInsights = (normalizedResponse.pricingInsights ?? null) as PricingInsightsData | null;
  const errorMessage = typeof error === 'string' && error.trim() ? error.trim() : null;

  if (loading && !hasLiveBreakdown) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 2,
          px: 1,
          borderRadius: 1.5,
          border: `1px dashed ${alpha(theme.palette.divider, 0.55)}`,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}>
        <CircularProgress size={20} thickness={5} />
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {strings.new_booking_venue_details_page_summary_loading}
        </Typography>
      </Box>
    );
  }

  if (errorMessage && !hasLiveBreakdown) {
    return (
      <Box
        sx={{
          py: 1.5,
          px: 1,
          borderRadius: 1.5,
          border: `1px dashed ${alpha(theme.palette.error.main, 0.5)}`,
          bgcolor: alpha(theme.palette.error.main, 0.04),
        }}>
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
          {strings.new_booking_venue_details_page_summary_title}
        </Typography>
        <Alert severity="error" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem', lineHeight: 1.45 } }}>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  if (!hasLiveBreakdown) {
    return (
      <Box
        sx={{
          py: 1.5,
          px: 1,
          borderRadius: 1.5,
          border: `1px dashed ${alpha(theme.palette.divider, 0.55)}`,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}>
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
          {strings.new_booking_venue_details_page_summary_title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {strings.new_booking_venue_details_page_summary_empty}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {loading ? (
        <CircularProgress
          size={16}
          thickness={5}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 1,
          }}
        />
      ) : null}
      <CostingComponent
        pageResponse={normalizedResponse}
        reviewLayout
        compact
        showStatusChip
        title={strings.new_booking_venue_details_page_summary_title}
      />
      {pricingInsights ? <PricingInsights insights={pricingInsights} /> : null}
    </Box>
  );
};

export default VenueDetailsPageSummary;
